import { useCallback, useState, type CSSProperties, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import type { HostedPhoto, Photo } from "../data/types";
import "./TreePhoto.css";

const FORMATS = ["avif", "webp", "jpeg"] as const;

function nearestWidth(photo: HostedPhoto, target: number): number | null {
  if (photo.widths.length === 0) return null;
  const notLarger = photo.widths.filter((width) => width <= target);
  return notLarger.length ? Math.max(...notLarger) : Math.min(...photo.widths);
}

export function photoUrl(photo: Photo, target = 1920): string {
  if (photo.kind === "external") return photo.url;
  const width = nearestWidth(photo, target) ?? Math.max(...photo.widths);
  return `${photo.basePath}-${width}.jpeg`;
}

function srcSet(photo: HostedPhoto, format: (typeof FORMATS)[number], target: number): string {
  const withinTarget = photo.widths.filter((width) => width <= target);
  const widths = withinTarget.length ? withinTarget : photo.widths.slice(0, 1);
  return widths.map((width) => `${photo.basePath}-${width}.${format} ${width}w`).join(", ");
}

export function PhotoFallback({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={`photo-fallback${compact ? " photo-fallback-compact" : ""}`} role="img" aria-label={t("photo.unavailable")}>
      <img className="brand-mark" src="/icons/brand-mark.png" alt="" />
      {!compact && <span>{t("photo.unavailable")}</span>}
    </div>
  );
}

export function TreePhoto({
  photo,
  alt,
  target = 960,
  sizes = "100vw",
  className,
  compactFallback = false,
  loading = "lazy",
  fetchPriority,
  onLoad,
}: {
  photo: Photo;
  alt: string;
  target?: number;
  sizes?: string;
  className?: string;
  compactFallback?: boolean;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
  /** For a caller that needs the frame's real shape: external photos carry no dimensions. */
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
}) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  /**
   * The frame is loading until the *current* photograph has loaded — a src, not a boolean,
   * because the lightbox swaps `photo` under one mounted TreePhoto. A boolean would stay true
   * from the first frame on and every later frame would arrive with no placeholder at all.
   */
  const [settled, setSettled] = useState<string | null>(null);
  const source = photo.kind === "external" ? photo.url : photo.basePath;
  const markLoaded = (event: SyntheticEvent<HTMLImageElement>) => {
    setSettled(source);
    onLoad?.(event);
  };
  /* An image served from cache can finish before React attaches onLoad; ask the node itself. */
  const settle = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth) setSettled(source);
  }, [source]);

  if (failedSource === source) return <PhotoFallback compact={compactFallback} />;

  const frame = (waiting: boolean) => `photo-frame${className ? ` ${className}` : ""}${waiting ? "" : " is-loaded"}`;

  if (photo.kind === "external") {
    /* Wrapped in a <picture> it does not need, so both kinds present the same frame element
       to the call-site CSS — and so a remote photograph gets the spinner too. */
    return (
      <picture className={frame(settled !== source)}>
        <img
          src={photo.url}
          width={photo.width}
          height={photo.height}
          alt={alt}
          loading={loading}
          fetchPriority={fetchPriority}
          referrerPolicy="no-referrer"
          ref={settle}
          onLoad={markLoaded}
          onError={() => setFailedSource(source)}
        />
      </picture>
    );
  }

  const fallback = nearestWidth(photo, target);
  /* The blur is a 16px LQIP. It reaches the image through a custom property rather than
     `background-image` directly, so TreePhoto.css can size it — an unsized background tiles,
     which is what turned the placeholder into a grid of tiny thumbnails. */
  const placeholder = { "--photo-blur": `url(${photo.blur})` } as CSSProperties;
  return (
    <picture className={frame(fallback !== null && settled !== source)} style={placeholder}>
      {FORMATS.slice(0, -1).map((format) => (
        <source key={format} type={`image/${format}`} srcSet={srcSet(photo, format, target)} sizes={sizes} />
      ))}
      <img
        src={fallback === null ? undefined : `${photo.basePath}-${fallback}.jpeg`}
        srcSet={srcSet(photo, "jpeg", target)}
        sizes={sizes}
        width={photo.width}
        height={photo.height}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        ref={settle}
        onLoad={markLoaded}
        onError={() => setFailedSource(source)}
      />
    </picture>
  );
}

export function photoSourceName(photo: Photo): string {
  if (photo.credit.kind === "team") return "";
  const sourceHost = photo.kind === "external" ? photo.sourceHost : new URL(photo.credit.sourceUrl).hostname.replace(/^www\./, "");
  const host = sourceHost === "google.com" ? "Google Maps" : sourceHost;
  return host;
}

export function PhotoCredit({ photo }: { photo: Photo }) {
  const { t } = useTranslation();
  if (photo.credit.kind === "team") return <span>{t("photo.creditTeam")}</span>;
  const host = photoSourceName(photo);
  return (
    <a href={photo.credit.sourceUrl} target="_blank" rel="noreferrer noopener">
      {t("photo.creditVia", { host })}
    </a>
  );
}
